import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { api, ApiError } from "../api/client";
import type { MenuCategory, MenuItem } from "../api/types";
import Footer from "../components/Footer";
import OpenBadge from "../components/OpenBadge";
import Reveal from "../components/Reveal";
import SiteNav from "../components/SiteNav";
import { useBusiness } from "../data/BusinessContext";
import { useLanguage } from "../i18n/LanguageContext";
import { imageUrl, srcSet } from "../lib/images";
import { formatPrice, formatPriceDelta, toCents } from "../lib/money";
import "./MenuPage.css";

/**
 * The menu as a printed card: categories down the page and a sticky strip
 * of chips that follows the section under the reader. Nothing to tap —
 * the only thing to do with this card is decide to come in.
 */
export default function MenuPage() {
  const { t, language, locale } = useLanguage();
  const business = useBusiness();

  const [categories, setCategories] = useState<MenuCategory[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState("");

  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  const load = useCallback(() => {
    setError(null);
    api
      .menu(language)
      .then((menu) => {
        setCategories(menu);
        setActiveCategory((current) => current || menu[0]?.slug || "");
      })
      .catch((err: ApiError) => setError(err.message));
  }, [language]);

  useEffect(load, [load]);

  // Highlight the chip for whatever section sits under the strip.
  useEffect(() => {
    if (!categories?.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible?.target instanceof HTMLElement && visible.target.dataset.slug) {
          setActiveCategory(visible.target.dataset.slug);
        }
      },
      { rootMargin: "-160px 0px -60% 0px", threshold: 0 },
    );
    for (const node of Object.values(sectionRefs.current)) if (node) observer.observe(node);
    return () => observer.disconnect();
  }, [categories]);

  const scrollToCategory = (slug: string) => {
    setActiveCategory(slug);
    const node = sectionRefs.current[slug];
    if (!node) return;
    const top = node.getBoundingClientRect().top + window.scrollY - 150;
    window.scrollTo({ top, behavior: "smooth" });
  };

  return (
    <div className="menu-page">
      <SiteNav />

      <header className="menu-hero section-tight" id="main">
        <div className="site-inner">
          <Reveal className="menu-hero-inner">
            <p className="eyebrow">{t("menuEyebrow")}</p>
            <h1 className="menu-title">{t("menuTitle")}</h1>
            <p className="lead">{t("menuLead")}</p>
            <div className="menu-hero-meta">
              <OpenBadge hours={business.hours} />
              <span className="tiny menu-note">{t("menuNote")}</span>
            </div>
          </Reveal>
        </div>
      </header>

      {error && (
        <div className="site-inner menu-state">
          <p>{t("somethingWrong")}</p>
          <p className="tiny">{error}</p>
          <button className="btn btn-outline" onClick={load}>
            {t("retry")}
          </button>
        </div>
      )}

      {!categories && !error && <MenuSkeleton />}

      {categories && (
        <>
          <nav className="category-strip" aria-label={t("navMenu")}>
            <div className="site-inner category-strip-inner">
              {categories.map((category) => (
                <button
                  key={category.slug}
                  className={`chip${category.slug === activeCategory ? " is-active" : ""}`}
                  onClick={() => scrollToCategory(category.slug)}
                  aria-current={category.slug === activeCategory}
                >
                  <DaypartMark daypart={category.daypart} />
                  {category.name}
                </button>
              ))}
            </div>
          </nav>

          <main className="site-inner menu-body">
            {categories.map((category) => (
              <section
                key={category.slug}
                data-slug={category.slug}
                ref={(node) => {
                  sectionRefs.current[category.slug] = node;
                }}
                className="menu-section"
              >
                <Reveal className="menu-section-head">
                  <h2 className="menu-section-title">{category.name}</h2>
                  {category.description && <p className="menu-section-desc">{category.description}</p>}
                </Reveal>

                <ul className="item-list">
                  {category.items.map((item, index) => (
                    <Reveal key={item.id} delay={Math.min(index, 4) * 0.05} y={18}>
                      <li>
                        <ItemRow item={item} locale={locale} />
                      </li>
                    </Reveal>
                  ))}
                </ul>
              </section>
            ))}

            <Reveal className="menu-cta">
              <p className="display menu-cta-text">{t("manifestoQuote")}</p>
              <Link className="btn btn-primary btn-lg" to="/reserve">
                {t("heroCtaReserve")} <span className="arrow">→</span>
              </Link>
            </Reveal>
          </main>
        </>
      )}

      <Footer business={business} />
    </div>
  );
}

function ItemRow({ item, locale }: { item: MenuItem; locale: string }) {
  const { t } = useLanguage();

  return (
    <article className={`item-row${item.image ? " has-photo" : ""}${item.is_available ? "" : " is-soldout"}`}>
      {item.image && (
        <span className="item-thumb photo photo-zoom">
          <img src={imageUrl(item.image, 200)} srcSet={srcSet(item.image, [200, 400])} sizes="84px" alt="" loading="lazy" decoding="async" />
        </span>
      )}

      <div className="item-main grow">
        <div className="item-heading">
          <h3 className="item-name">{item.name}</h3>
          <span className="item-leader" aria-hidden="true" />
          <span className="item-price nums">{formatPrice(toCents(item.price), locale)}</span>
        </div>

        {item.description && <p className="item-description">{item.description}</p>}

        {/* Choices read like a printed card: "Milk: whole · oat +0,50 € · almond +0,50 €". */}
        {item.modifier_groups.map((group) => (
          <p key={group.id} className="item-choices tiny">
            <span className="item-choices-label">{group.name}:</span>{" "}
            {group.options.map((option, index) => {
              const extra = toCents(option.price);
              return (
                <span key={option.id}>
                  {index > 0 && " · "}
                  {option.name}
                  {extra > 0 && <span className="item-choice-price nums"> {formatPriceDelta(extra, locale)}</span>}
                </span>
              );
            })}
          </p>
        ))}

        <div className="item-tags">
          {item.is_favourite && <span className="pill pill-bordeaux">{t("houseFavourite")}</span>}
          {item.is_seasonal && <span className="pill pill-brass">{t("seasonal")}</span>}
          {item.diet && <span className="pill pill-green">{t(item.diet)}</span>}
          {!item.is_available && <span className="pill">{t("soldOut")}</span>}
          {item.allergens && <span className="item-allergens">{t("allergens")}: {item.allergens}</span>}
        </div>
      </div>
    </article>
  );
}

/** A small sun / half-sun / moon, so a glance says when a category belongs. */
function DaypartMark({ daypart }: { daypart: MenuCategory["daypart"] }) {
  if (daypart === "all") return null;
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      {daypart === "morning" && (
        <>
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M4.9 19.1L7 17M17 7l2.1-2.1" />
        </>
      )}
      {daypart === "afternoon" && (
        <>
          <path d="M4 16a8 8 0 0 1 16 0" />
          <path d="M2 20h20M12 4v2M5 7l1.5 1.5M19 7l-1.5 1.5" />
        </>
      )}
      {daypart === "evening" && <path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z" />}
    </svg>
  );
}

function MenuSkeleton() {
  return (
    <div className="site-inner menu-body" aria-hidden="true">
      {[0, 1, 2].map((section) => (
        <div key={section} className="menu-section">
          <div className="skeleton skeleton-title" />
          {[0, 1, 2].map((row) => (
            <div key={row} className="skeleton skeleton-row" />
          ))}
        </div>
      ))}
    </div>
  );
}
